import React, { useState } from "react";
import { Box, Tabs, Tab, Typography } from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { Refresh as RefreshIcon, FileUpload as PublishIcon } from "@mui/icons-material";
import Header from "../Header";
import { DashboardContainer } from "./components/StyledComponents";
import GenerateForm from "./components/GenerateForm";
import StagingTab from "./components/StagingTab";
import ImportedQuestionsList from "./components/ImportedQuestionsList";
import { useAiQuestions } from "./hooks/useAiQuestions";

const AiQuestionDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [simpleMode, setSimpleMode] = useState(false);

  const {
    // State
    lookups,
    formState,
    loadingGenerate,
    batches,
    selectedBatch,
    batchQuestions,
    batchLoading,
    selectedTempQuestions,
    importedQuestions,
    importedLoading,
    selectedBatchConfig,
    questionClues,
    selectedSongsForClues,
    generatingClues,
    filteredSongs,
    songsLoading,
    groupedImportedByBatch,
    // Actions
    handleFormChange,
    handleGenerate,
    updateQuestionStatus,
    handlePublishSelected,
    toggleTempQuestionSelection,
    handleBulkSelectSongsForClues,
    fetchQuestionClues,
    handleGenerateClues,
    handleDeleteClue,
    handleBatchChange,
    fetchBatches,
    fetchBatchQuestions,
    fetchImported,
    setSelectedSongsForClues,
  } = useAiQuestions();

  const handleGenerateAndSwitch = async () => {
    const success = await handleGenerate();
    if (success) {
      setActiveTab(1);
    }
  };

  const handleRefreshBatches = () => {
    fetchBatches();
    if (selectedBatch) {
      fetchBatchQuestions(selectedBatch);
    }
  };

  const handleApprove = (tempQuestionId) => {
    updateQuestionStatus(tempQuestionId, "approved_for_live");
  };

  const handleReject = (tempQuestionId) => {
    updateQuestionStatus(tempQuestionId, "rejected");
  };

  const handleSongsForCluesChange = (tempQuestionId, songIds) => {
    setSelectedSongsForClues((prev) => ({
      ...prev,
      [tempQuestionId]: songIds,
    }));
  };

  return (
    <>
      <Header />
      <DashboardContainer>
        <Box mb={3}>
          <Typography variant="h3" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
            AI Question Generation
          </Typography>
          <Typography variant="h6" sx={{ color: "#ccc", fontWeight: 400 }}>
            Generate, review, and publish AI-assisted music quiz questions
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          textColor="secondary"
          indicatorColor="secondary"
          sx={{ mb: 3 }}
        >
          <Tab label="Generate" icon={<SmartToyIcon />} iconPosition="start" />
          <Tab label="Staging" icon={<RefreshIcon />} iconPosition="start" />
          <Tab label="Imported" icon={<PublishIcon />} iconPosition="start" />
        </Tabs>

        {activeTab === 0 && (
          <GenerateForm
            formState={formState}
            lookups={lookups}
            loadingGenerate={loadingGenerate}
            handleFormChange={handleFormChange}
            handleGenerate={handleGenerateAndSwitch}
          />
        )}

        {activeTab === 1 && (
          <StagingTab
            batches={batches}
            selectedBatch={selectedBatch}
            selectedBatchConfig={selectedBatchConfig}
            batchQuestions={batchQuestions}
            batchLoading={batchLoading}
            selectedTempQuestions={selectedTempQuestions}
            simpleMode={simpleMode}
            onSimpleModeChange={setSimpleMode}
            onBatchChange={handleBatchChange}
            onRefreshBatches={handleRefreshBatches}
            onToggleSelection={toggleTempQuestionSelection}
            onApprove={handleApprove}
            onReject={handleReject}
            onPublishSelected={handlePublishSelected}
            filteredSongs={filteredSongs}
            songsLoading={songsLoading}
            questionClues={questionClues}
            selectedSongsForClues={selectedSongsForClues}
            generatingClues={generatingClues}
            onBulkSelectSongsForClues={handleBulkSelectSongsForClues}
            onSongsForCluesChange={handleSongsForCluesChange}
            onGenerateClues={handleGenerateClues}
            onRefreshClues={fetchQuestionClues}
            onDeleteClue={handleDeleteClue}
          />
        )}

        {activeTab === 2 && (
          <ImportedQuestionsList
            importedQuestions={importedQuestions}
            groupedImportedByBatch={groupedImportedByBatch}
            importedLoading={importedLoading}
            onRefresh={fetchImported}
          />
        )}
      </DashboardContainer>
    </>
  );
};

export default AiQuestionDashboard;
